import moment from 'moment'
import TrainingItemMonth from './TrainingItemMonth'

const getTrainingRangeForWeek = (training, week) => {
	const weekStart = moment(week[0]?.date, 'YYYY-MM-DD', true)
	const weekEnd = moment(week[week.length - 1]?.date, 'YYYY-MM-DD', true)
	const trainingStart = moment(training?.trainingDate, moment.ISO_8601, true)
	const trainingEnd = moment(training?.trainingEndDate || training?.trainingDate, moment.ISO_8601, true)

	if (!weekStart.isValid() || !weekEnd.isValid() || !trainingStart.isValid() || !trainingEnd.isValid()) {
		return null
	}

	const visibleStart = moment.max(trainingStart, weekStart)
	const visibleEnd = moment.min(trainingEnd, weekEnd)
	if (visibleEnd.isBefore(visibleStart, 'day')) {
		return null
	}

	return {
		training,
		visibleStart,
		visibleEnd,
	}
}

const buildTrainingTracks = (week, trainings = []) => {
	const weekTrainings = trainings
		.map(training => getTrainingRangeForWeek(training, week))
		.filter(Boolean)
		.sort((left, right) => {
			const leftDate = left.visibleStart.format('YYYY-MM-DD')
			const rightDate = right.visibleStart.format('YYYY-MM-DD')
			if (leftDate !== rightDate) {
				return leftDate.localeCompare(rightDate, 'pl')
			}

			return `${left.training?.trainingName || ''}`.localeCompare(`${right.training?.trainingName || ''}`, 'pl')
		})

	const trackEndDates = []

	return weekTrainings.map(item => {
		const availableTrackIndex = trackEndDates.findIndex(trackEnd =>
			item.visibleStart.isAfter(trackEnd, 'day')
		)

		const trackIndex = availableTrackIndex === -1 ? trackEndDates.length : availableTrackIndex
		trackEndDates[trackIndex] = item.visibleEnd.clone()

		return {
			...item,
			trackIndex,
		}
	})
}

const getTrainingInsetClass = ({ isSingleDay, isRangeStart, isRangeEnd }) => {
	if (isSingleDay || (isRangeStart && isRangeEnd)) {
		return 'px-2'
	}

	if (isRangeStart) {
		return 'pl-2 pr-0'
	}

	if (isRangeEnd) {
		return 'pl-0 pr-2'
	}

	return 'px-0'
}

function MonthWeekRow({ week, trainings, onTrainingClick }) {
	const trainingTracks = buildTrainingTracks(week, trainings)
	const groupedTrainingTracks = trainingTracks.reduce((acc, item) => {
		acc[item.trackIndex] = [...(acc[item.trackIndex] || []), item]
		return acc
	}, [])
	const trainingAreaTop = 38
	const trainingRowHeight = 28
	const minWeekHeight = 128
	const trainingTrackCount = Math.max(groupedTrainingTracks.length, 1)
	const minCellHeight = Math.max(
		minWeekHeight,
		trainingAreaTop + trainingTrackCount * trainingRowHeight + 22
	)

	return (
		<div className="relative grid grid-cols-7">
			{week.map(day => {
				const isToday = moment(day.date, 'YYYY-MM-DD', true).isSame(moment(), 'day')

				return (
					<div
						key={day.date}
						style={{ minHeight: `${minCellHeight}px` }}
						className={`relative overflow-visible border-r border-gray-200 p-2 last:border-r-0 ${
							isToday ? 'bg-gray-100' : !day.isCurrentMonth ? 'bg-gray-50' : ''
						}`}>
						<div
							className={`mb-1 text-sm font-medium ${
								isToday ? 'text-gray-950' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
							}`}>
							{day.day}
						</div>
					</div>
				)
			})}

			{groupedTrainingTracks.length > 0 && (
				<div className="pointer-events-none absolute inset-x-0" style={{ top: `${trainingAreaTop}px` }}>
					{groupedTrainingTracks.map((trackItems, trackIndex) => (
						<div
							key={`training-track-row-${trackIndex}`}
							className="grid grid-cols-7"
							style={{ height: `${trainingRowHeight}px` }}>
							{trackItems.map(track => {
								const startColumn = week.findIndex(day => moment(day.date, 'YYYY-MM-DD', true).isSame(track.visibleStart, 'day')) + 1
								const endColumn = week.findIndex(day => moment(day.date, 'YYYY-MM-DD', true).isSame(track.visibleEnd, 'day')) + 1
								const span = Math.max(endColumn - startColumn + 1, 1)
								const trainingStart = moment(track.training?.trainingDate, moment.ISO_8601, true)
								const trainingEnd = moment(track.training?.trainingEndDate || track.training?.trainingDate, moment.ISO_8601, true)
								const isSingleDay = track.visibleStart.isSame(track.visibleEnd, 'day')
								const isRangeStart = track.visibleStart.isSame(trainingStart, 'day')
								const isRangeEnd = track.visibleEnd.isSame(trainingEnd, 'day')
								const insetClass = getTrainingInsetClass({ isSingleDay, isRangeStart, isRangeEnd })
								const trainingId = String(track.training?._id || track.training?.id || `training-track-${track.trackIndex}`)
								const tooltipParts = [
									track.training?.trainingName || '—',
									track.training?.placowkaName || '',
									track.training?.placowkaWojewodztwo || '',
								].filter(Boolean)

								return (
									<div
										key={`${trainingId}-${track.visibleStart.format('YYYY-MM-DD')}-${track.visibleEnd.format('YYYY-MM-DD')}`}
										className={`pointer-events-auto cursor-pointer ${insetClass}`}
										style={{ gridColumn: `${startColumn} / span ${span}` }}
										onClick={event => {
											event.stopPropagation()
											onTrainingClick(track.training)
										}}>
										<TrainingItemMonth
											title={track.training?.trainingName || '—'}
											tooltip={tooltipParts.join(' • ')}
											isSingleDay={isSingleDay}
											isRangeStart={isRangeStart}
											isRangeEnd={isRangeEnd}
										/>
									</div>
								)
							})}
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default MonthWeekRow
